import { TenantConfigSection } from '@today/types'
import type { ComponentType } from 'react'
import type { TenantConfigSectionProps } from './section-props'
import { BrandSection } from './sections/BrandSection'
import { EmailSection } from './sections/EmailSection'
import { FileExchangeSection } from './sections/FileExchangeSection'
import { GeneralSection } from './sections/GeneralSection'
import { OrganizationSection } from './sections/OrganizationSection'
import { PdfWatermarkSection } from './sections/PdfWatermarkSection'
import { SeedingSection } from './sections/SeedingSection'
import { SmsSection } from './sections/SmsSection'

// The props type lives in its own module so a section component can import it
// without importing the registry that imports the section (a module cycle).
export type { TenantConfigSectionProps } from './section-props'

export interface TenantConfigSectionEntry {
	title: string
	/** The owning story's page; absent → the "not available yet" placeholder. */
	component?: ComponentType<TenantConfigSectionProps>
}

export type TenantConfigSectionRegistry = Record<TenantConfigSection, TenantConfigSectionEntry>

/**
 * The console's section registry. Each section story (T1…T8) registers its page
 * by adding `component` to its entry. Visibility and access are NOT decided
 * here — the server's manifest does that; the registry only says what to render.
 */
export const TENANT_CONFIG_SECTION_REGISTRY: TenantConfigSectionRegistry = {
	[TenantConfigSection.GENERAL]: { title: 'General', component: GeneralSection },
	[TenantConfigSection.SEEDING]: { title: 'Seeding status', component: SeedingSection },
	[TenantConfigSection.IDENTITY]: { title: 'Identity & login' },
	[TenantConfigSection.ORGANIZATION]: { title: 'Organization', component: OrganizationSection },
	[TenantConfigSection.FILE_EXCHANGE]: { title: 'File exchange', component: FileExchangeSection },
	[TenantConfigSection.BRAND]: { title: 'Brand', component: BrandSection },
	[TenantConfigSection.PDF_WATERMARK]: { title: 'PDF watermark', component: PdfWatermarkSection },
	[TenantConfigSection.WEBHOOKS]: { title: 'Webhooks' },
	[TenantConfigSection.EMAIL]: { title: 'Email', component: EmailSection },
	[TenantConfigSection.SMS]: { title: 'SMS', component: SmsSection },
	[TenantConfigSection.DOMAIN]: { title: 'Domain' },
	[TenantConfigSection.WHATSAPP]: { title: 'WhatsApp' },
}

/** The section's registered component, or `null` when the placeholder should render. */
export function resolveSectionComponent(
	registry: TenantConfigSectionRegistry,
	section: TenantConfigSection,
): ComponentType<TenantConfigSectionProps> | null {
	return registry[section].component ?? null
}
